import type { EsignChatSummary, EsignChatSummaryRow, SignatureField, ToolResult } from '../types';

export function buildEsignChatSummary(
  fields: SignatureField[],
  result: ToolResult
): EsignChatSummary {
  const sorted = [...fields].sort((a, b) => a.page - b.page || a.y - b.y);
  const total = sorted.length;
  const signedByYou = sorted.filter((f) => f.signer !== 'other' && f.placed).length;
  const headerTitle = `Signature applied — ${signedByYou} of ${total}`;
  const rows: EsignChatSummaryRow[] = sorted.map((f) => {
    const isOther = f.signer === 'other';
    const signed = !isOther && f.placed;
    const base = f.label.trim();
    const labelWithRole =
      isOther ? base : /\((you)\)/i.test(base) ? base : `${base} (you)`;
    return {
      labelLine: `${labelWithRole} · Page ${f.page}`,
      status: signed ? 'signed' : 'awaiting',
    };
  });
  return {
    headerTitle,
    rows,
    downloadCtaLabel: 'Download signed PDF',
    fileMetaLine: `${result.fileName} · ${formatBytes(result.sizeBytes)}`,
    downloadUrl: result.downloadUrl,
    fileName: result.fileName,
  };
}

function formatBytes(bytes: number): string {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isRow(x: unknown): x is EsignChatSummaryRow {
  if (!x || typeof x !== 'object') return false;
  const o = x as Record<string, unknown>;
  return (
    typeof o.labelLine === 'string' &&
    (o.status === 'signed' || o.status === 'awaiting')
  );
}

/** Restore summary from API / localStorage toolCall.params */
export function parseEsignSummary(raw: unknown): EsignChatSummary | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const o = raw as Record<string, unknown>;
  if (typeof o.headerTitle !== 'string' || !Array.isArray(o.rows)) return undefined;
  const rows = o.rows.filter(isRow);
  if (rows.length !== o.rows.length) return undefined;
  const fileMetaLine = typeof o.fileMetaLine === 'string' ? o.fileMetaLine : '';
  const downloadCtaLabel =
    typeof o.downloadCtaLabel === 'string' ? o.downloadCtaLabel : 'Download signed PDF';
  return {
    headerTitle: o.headerTitle,
    rows,
    downloadCtaLabel,
    fileMetaLine,
  };
}
